import React, { useEffect, useState } from "react";
import { FormField } from "@avamae/formbuilder";
import { useField, useFormikContext } from "formik";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";

import editIcon from "assets/edit_icon.png";

const tooltipMainIconSource = require("assets/Tooltip.png");
const tooltipCloseIconSource = require("assets/Icon_Btn_06.png");

type TextAreaFieldRendererProps = {
  name: string;
  dataSource: string | null;
  label: string;
  tooltip: string | null;
  readOnly: boolean;
  placeholder?: string;
  maxCharacterLimit?: number;
  editReadOnly?: boolean;
  showEditIcon?: boolean;
};

const TextAreaFieldRenderer: React.FC<TextAreaFieldRendererProps> = ({
  name,
  dataSource,
  label,
  tooltip,
  readOnly,
  placeholder,
  maxCharacterLimit,
  editReadOnly,
  showEditIcon,
}) => {
  const [field] = useField<any>(name);
  const [value, setValue] = useState<any>(field.value);

  useEffect(() => {
    setValue(field.value);
  }, [field.value]);

  const [relevantFormikError, setRelevantFormikError] = useState<any>(null);

  const {
    values,
    errors,
    setFieldValue,
    submitCount,
    setFieldError,
    setErrors,
  } = useFormikContext<{}>();

  useEffect(() => {
    if (errors.hasOwnProperty(field.name)) {
      const error = errors[field.name as keyof typeof errors];

      if (typeof error === "string") {
        setRelevantFormikError(errors[field.name as keyof typeof errors]);
      }
    }
  }, [errors]);

  return (
    <>
      {readOnly ? (
        <div className={`FormBox FormSideBySide TextAreaFieldRenderer ${name}`}>
          <div className="FormLabel">
            <label>{label}</label>

            {tooltip && (
              <FormFieldTooltip
                tooltipText={tooltip}
                tooltipMainIconSource={tooltipMainIconSource}
                tooltipCloseIconSource={tooltipCloseIconSource}
              />
            )}
          </div>

          <div className={`FormField TextAreaFieldRenderer`}>
            {value && typeof value === "string" && value.trim() !== ""
              ? value
              : "-"}
          </div>
        </div>
      ) : (
        <div
          className={`FormBox FormSideBySide TextAreaFieldRenderer ${name} ${
            editReadOnly ? "EditReadOnly" : ""
          }`}
        >
          <div className={`FormLabel`}>
            <label>
              {label}

              {showEditIcon && (
                <img src={editIcon} alt="edit icon" className="EditIcon" />
              )}
            </label>

            {tooltip && (
              <FormFieldTooltip
                tooltipText={tooltip}
                tooltipMainIconSource={tooltipMainIconSource}
                tooltipCloseIconSource={tooltipCloseIconSource}
              />
            )}
          </div>
          <div
            className={`FormField TextAreaFieldRenderer 
          Single
        `}
          >
            <div
              className={`TextAreaFieldRenderer ${
                relevantFormikError ? "Error" : ""
              }`}
            >
              {maxCharacterLimit && (
                <div className="MaxCharacterLimit">
                  Maximum text length is{" "}
                  {maxCharacterLimit &&
                  typeof maxCharacterLimit === "number" &&
                  value &&
                  typeof value === "string"
                    ? maxCharacterLimit - value.length >= 0
                      ? `${maxCharacterLimit - value.length} characters`
                      : "exceeded"
                    : `${maxCharacterLimit} characters`}{" "}
                </div>
              )}

              <textarea
                className="EditBox"
                name={name}
                value={value}
                onChange={e => {
                  {
                    if (!editReadOnly) {
                      setValue(e.target.value);
                    }
                  }
                }}
                placeholder={placeholder}
                onBlur={e => {
                  if (!editReadOnly) {
                    setFieldValue(name, e.target.value);
                  }
                }}
              />
            </div>

            {relevantFormikError && submitCount > 0 && (
              <div className="ErrorLabel">{relevantFormikError}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export const textAreaFieldRenderer = (
  readOnly: boolean,
  overwritePlaceholder?: { [field: string]: string },
  maxCharacterLimit?: { [field: string]: number },
  editReadOnly?: boolean,
  showEditIcon?: boolean
) =>
  function textAreaFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    const finalOverwritePlaceholder =
      overwritePlaceholder && overwritePlaceholder.hasOwnProperty(field.name)
        ? overwritePlaceholder[field.name as keyof typeof overwritePlaceholder]
        : null;
    const finalMaxCharacterLimit =
      maxCharacterLimit && maxCharacterLimit.hasOwnProperty(field.name)
        ? maxCharacterLimit[field.name as keyof typeof maxCharacterLimit]
        : null;
    return (
      <TextAreaFieldRenderer
        name={String(field.name)}
        dataSource={field.dataSource ? field.dataSource : null}
        label={field.label as string}
        tooltip={field.toolTip ? field.toolTip : null}
        readOnly={readOnly}
        // @ts-ignore - the package doesn't have the placeholder type
        placeholder={finalOverwritePlaceholder ?? undefined}
        maxCharacterLimit={finalMaxCharacterLimit ?? undefined}
        editReadOnly={editReadOnly ?? undefined}
        showEditIcon={showEditIcon}
      />
    );
  };
